
import React, { createContext, useEffect, useState, useCallback, useContext } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from './config';
import { JartinetHermandad, Banda, Organizacion, UserProfile, Empresa, Carta } from '@/lib/types';
import { httpsCallable } from 'firebase/functions';

export interface AuthContextType {
  user: User | null;
  userData: UserProfile | null;
  loading: boolean;
  jartinetData: {
    hermandades: JartinetHermandad[];
    bandas: Banda[];
    organizaciones: Organizacion[];
    empresas: Empresa[];
    userProfiles: Record<string, UserProfile>;
  };
  refetch: () => Promise<void>;
  handleActionCompletion: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getJartinetData = httpsCallable(functions, 'getJartinetData');

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [jartinetData, setJartinetData] = useState<AuthContextType['jartinetData']>({
    hermandades: [],
    bandas: [],
    organizaciones: [],
    empresas: [],
    userProfiles: {},
  });

  const fetchAllData = useCallback(async (firebaseUser: User | null) => {
    if (firebaseUser) {
      try {
        // `loading` no puede bajar antes de tener el perfil: las páginas de admin miran
        // `!loading && userData?.role !== 'admin'` y, si getJartinetData volvía antes que
        // el primer snapshot, echaban al admin a /jartinet al abrirlas directamente.
        let perfilListo: () => void = () => {};
        const primerPerfil = new Promise<void>((resolve) => { perfilListo = resolve; });
        const userDocUnsubscribe = onSnapshot(doc(db, 'users', firebaseUser.uid), (doc) => {
           if (doc.exists()) {
              setUserData(doc.data() as UserProfile);
           } else {
              setUserData(null);
           }
           perfilListo();
        }, (error) => {
           console.error("Error leyendo el perfil:", error);
           setUserData(null);
           perfilListo();
        });

        // Llamar a la Cloud Function para obtener los datos
        const [result] = await Promise.all([getJartinetData(), primerPerfil]);
        const fetchedData = result.data as any;

        if (fetchedData.error) {
            console.error("Error from getJartinetData function:", fetchedData.error);
             setJartinetData({ hermandades: [], bandas: [], organizaciones: [], empresas: [], userProfiles: {} });
        } else {
            setJartinetData({
                hermandades: fetchedData.hermandades || [],
                bandas: fetchedData.bandas || [],
                organizaciones: fetchedData.organizaciones || [],
                empresas: fetchedData.empresas || [],
                userProfiles: fetchedData.userProfiles || {},
            });
        }
        return userDocUnsubscribe;
      } catch (e) {
        console.error("Error fetching all user and jartinet data:", e);
        setUserData(null);
        setJartinetData({ hermandades: [], bandas: [], organizaciones: [], empresas: [], userProfiles: {} });
      }
    } else {
      setUserData(null);
      setJartinetData({ hermandades: [], bandas: [], organizaciones: [], empresas: [], userProfiles: {} });
    }
  }, []);

  const refetch = useCallback(async () => {
    await fetchAllData(auth.currentUser);
  }, [fetchAllData]);

  const handleActionCompletion = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    let userDocUnsubscribe: (() => void) | undefined;
    
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      setUser(firebaseUser);
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
      userDocUnsubscribe = await fetchAllData(firebaseUser);
      setLoading(false);
    });

    return () => {
        unsubscribeAuth();
        if (userDocUnsubscribe) {
            userDocUnsubscribe();
        }
    };
  }, [fetchAllData]);

  const value = { user, userData, loading, jartinetData, refetch, handleActionCompletion };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
